import { Injectable, DestroyRef, inject, effect, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '@modules/auth/services/auth.service';
import {
  ConversationsSseService,
  SseEvent,
  isGuestDataDeletedEvent,
} from '@modules/conversations/services/conversations-sse.service';
import { ConversationsApiService } from '@modules/conversations/services/conversations-api.service';
import { ConversationsService } from '@modules/conversations/services/conversations.service';

/**
 * Powód `request.withdrawn` (backend: `data.reason`, RequestFanoutService.notifyDeleted):
 * - `deleted` — gość usunął wpis (RODO art. 17 ust. 2): host ma nie przetwarzać dalej jego danych;
 * - `closed` — wpis zamknięty (gość znalazł nocleg / zrezygnował / auto-close po rezerwacji).
 * W obu przypadkach propozycje do wpisu są wycofane, a wpis znika z list.
 */
export type RequestWithdrawnReason = 'deleted' | 'closed';

/**
 * Powód wycofania wpisu z payloadu SSE. Brak pola (starszy backend) i wartości nieznane →
 * `deleted`: komunikat RODO jest bezpieczniejszy niż jego pominięcie.
 * (Parametr dopuszcza `reason` także wtedy, gdy `SseEvent` go jeszcze nie deklaruje.)
 */
export function requestWithdrawnReason(event: SseEvent & { reason?: unknown }): RequestWithdrawnReason {
  return event.reason === 'closed' ? 'closed' : 'deleted';
}

/**
 * K15: po usunięciu zapytania backend wysyła osobne `inquiry.closed` (reason `guest_data_deleted`) dla każdego
 * zanonimizowanego wątku, jedno po drugim. Seria w tym oknie daje jeden komunikat zamiast pustego
 * „Wątek zamknięty” na każdy wątek; każde kolejne zdarzenie serii odsuwa komunikat o to okno.
 */
export const GUEST_DATA_DELETED_TOAST_DEBOUNCE_MS = 1000;

/**
 * Manages SSE lifecycle tied to authentication state.
 * Connects on login, disconnects on logout.
 * Shows toast notifications for incoming SSE events.
 * Fetches initial unread count on connect/reconnect.
 */
@Injectable({
  providedIn: 'root',
})
export class SseNotificationService {
  private readonly authService = inject(AuthService);
  private readonly sseService = inject(ConversationsSseService);
  private readonly conversationsService = inject(ConversationsService);
  private readonly api = inject(ConversationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private initialized = false;
  private guestDataDeletedToastTimer: ReturnType<typeof setTimeout> | null = null;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.destroyRef.onDestroy(() => this.cancelGuestDataDeletedToast());

    // React to auth state changes
    this.authService.isAuthenticated$.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.sseService.connect();
        this.fetchUnreadCount();
      } else {
        this.sseService.disconnect();
        this.cancelGuestDataDeletedToast();
      }
    });

    // Show toast notifications on SSE events
    effect(() => {
      const event = this.sseService.lastEvent();
      if (event) {
        untracked(() => this.showNotification(event));
      }
    });

    // Refetch unread count on reconnect
    effect(() => {
      const connected = this.sseService.connected();
      if (connected) {
        untracked(() => this.fetchUnreadCount());
      }
    });
  }

  private fetchUnreadCount(): void {
    this.api.getUnreadCount().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => this.sseService.setUnreadCount(res.totalUnread),
    });
  }

  private showNotification(event: SseEvent): void {
    // Don't show notification if the user is currently viewing this thread
    const selectedThreadId = this.conversationsService.selectedThread()?.id;
    if (event.threadId && event.threadId === selectedThreadId) {
      return;
    }

    switch (event.type) {
      case 'inquiry.new': {
        const guestName = event.guestName ?? '';
        const propertyName = event.propertyName ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.new'),
          detail: this.transloco.translate('notifications.inquiry.newDetail', { guestName, propertyName }),
          life: 5000,
        });
        break;
      }

      case 'inquiry.new_message':
      case 'conversation.new_message': {
        // Only notify about guest messages (not host's own messages)
        if (event.senderType === 'host') return;

        const guestName = event.guestName ?? '';
        const preview = event.messagePreview ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.newMessage'),
          detail: guestName
            ? this.transloco.translate('notifications.inquiry.newMessageDetail', { guestName, preview })
            : preview,
          life: 5000,
        });
        break;
      }

      case 'conversation.new': {
        const guestName = event.guestName ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.new'),
          detail: this.transloco.translate('notifications.inquiry.newDetail', {
            guestName,
            propertyName: event.propertyName ?? '',
          }),
          life: 5000,
        });
        break;
      }

      case 'inquiry.closed':
      case 'conversation.closed': {
        if (isGuestDataDeletedEvent(event)) {
          this.scheduleGuestDataDeletedToast();
          break;
        }
        const guestName = event.guestName ?? '';
        this.messageService.add({
          severity: 'warn',
          summary: this.transloco.translate('notifications.inquiry.closed'),
          detail: guestName
            ? this.transloco.translate('notifications.inquiry.closedDetail', { guestName })
            : '',
          life: 4000,
        });
        break;
      }

      case 'request.new_match': {
        const headline = event.headline ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.request.newMatch'),
          detail: headline ? this.transloco.translate('notifications.request.newMatchDetail', { headline }) : '',
          life: 6000,
        });
        break;
      }

      case 'request.proposal_replied': {
        // Payload: { requestId, proposalId, objectId, threadId } — bez nagłówka wpisu.
        this.messageService.add({
          severity: 'success',
          summary: this.transloco.translate('notifications.request.proposalReplied'),
          detail: this.transloco.translate('notifications.request.proposalRepliedDetail'),
          life: 6000,
        });
        break;
      }

      case 'request.withdrawn': {
        // Payload: { requestId, reason } — bez danych gościa. Komunikat RODO („nie przetwarzaj
        // dalej”) tylko przy usunięciu; zamknięcie wpisu to zwykła informacja.
        if (requestWithdrawnReason(event) === 'closed') {
          this.messageService.add({
            severity: 'info',
            summary: this.transloco.translate('notifications.request.closed'),
            detail: this.transloco.translate('notifications.request.closedDetail'),
            life: 6000,
          });
          break;
        }
        this.messageService.add({
          severity: 'warn',
          summary: this.transloco.translate('notifications.request.withdrawn'),
          detail: this.transloco.translate('notifications.request.withdrawnDetail'),
          life: 6000,
        });
        break;
      }

      // Presence events don't need toast notifications
      default:
        break;
    }
  }

  /** Jeden komunikat na serię zdarzeń K15 (patrz `GUEST_DATA_DELETED_TOAST_DEBOUNCE_MS`). */
  private scheduleGuestDataDeletedToast(): void {
    this.cancelGuestDataDeletedToast();
    this.guestDataDeletedToastTimer = setTimeout(() => {
      this.guestDataDeletedToastTimer = null;
      this.messageService.add({
        severity: 'warn',
        summary: this.transloco.translate('notifications.inquiry.closedGuestDataDeleted'),
        detail: this.transloco.translate('notifications.inquiry.closedGuestDataDeletedDetail'),
        life: 6000,
      });
    }, GUEST_DATA_DELETED_TOAST_DEBOUNCE_MS);
  }

  private cancelGuestDataDeletedToast(): void {
    if (this.guestDataDeletedToastTimer === null) return;
    clearTimeout(this.guestDataDeletedToastTimer);
    this.guestDataDeletedToastTimer = null;
  }
}
